import { Badge, Button } from "@devhop/ui";
import { Gift, Tag, X } from "lucide-react";
import {
  getCouponSnapshotSourceLabel,
  getPricingSourceLabel,
  getVoucherSnapshotSourceLabel,
  hasDiscountBreakdown,
} from "../lib/offline-sync/local-first-display-helpers";
import type {
  LocalOrderCouponSnapshot,
  LocalOrderDiscountBreakdown,
  LocalOrderPricingSource,
  LocalOrderRewardSnapshot,
  LocalOrderVoucherSnapshot,
} from "../lib/offline-sync/types";

type LocalOrderPricingPanelProps = {
  couponSnapshots?: LocalOrderCouponSnapshot[];
  voucherSnapshots?: LocalOrderVoucherSnapshot[];
  rewardSnapshot?: LocalOrderRewardSnapshot;
  discountBreakdown?: LocalOrderDiscountBreakdown;
  pricingSource?: LocalOrderPricingSource;
  canRemoveReward?: boolean;
  isRemovingReward?: boolean;
  onRemoveReward?: () => void;
};

export function LocalOrderPricingPanel({
  couponSnapshots,
  voucherSnapshots,
  rewardSnapshot,
  discountBreakdown,
  pricingSource,
  canRemoveReward = false,
  isRemovingReward = false,
  onRemoveReward,
}: LocalOrderPricingPanelProps) {
  const hasCoupons = (couponSnapshots?.length ?? 0) > 0;
  const hasVouchers = (voucherSnapshots?.length ?? 0) > 0;
  const hasReward = (rewardSnapshot?.discountAmount ?? 0) > 0;
  const hasBreakdown = hasDiscountBreakdown(discountBreakdown);

  if (
    !hasCoupons &&
    !hasVouchers &&
    !hasReward &&
    !hasBreakdown &&
    !pricingSource
  ) {
    return null;
  }

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium text-sm">
          <Tag className="h-4 w-4 text-muted-foreground" />
          ສ່ວນຫຼຸດ / Coupon
        </div>
        {pricingSource && (
          <Badge variant="outline">{getPricingSourceLabel(pricingSource)}</Badge>
        )}
      </div>

      {hasCoupons && (
        <div className="mt-3 space-y-2">
          <div className="text-muted-foreground text-xs">Coupon snapshots</div>
          <ul className="space-y-2">
            {couponSnapshots?.map((snapshot) => (
              <li
                key={`${snapshot.code}-${snapshot.validatedAt}`}
                className="rounded border bg-background p-2 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{snapshot.code}</span>
                  <Badge variant="outline" className="text-xs">
                    {getCouponSnapshotSourceLabel(snapshot.source)}
                  </Badge>
                </div>
                <div className="mt-1 text-muted-foreground text-xs">
                  -{snapshot.discountAmount.toLocaleString("lo-LA")} ₭ · scope{" "}
                  {snapshot.applyScope}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasReward && rewardSnapshot && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-muted-foreground text-xs">ລາງວັນ / ຄະແນນ</div>
            {canRemoveReward && onRemoveReward && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-destructive hover:text-destructive"
                onClick={onRemoveReward}
                disabled={isRemovingReward}
              >
                <X className="mr-1 h-3 w-3" />
                ຖອນລາງວັນ
              </Button>
            )}
          </div>
          <div className="rounded border border-violet-200 bg-violet-50/80 p-2 text-sm dark:border-violet-900 dark:bg-violet-950/40">
            <div className="flex flex-wrap items-center gap-2 font-medium">
              <Gift className="h-4 w-4 text-violet-700 dark:text-violet-300" />
              {rewardSnapshot.rewardName}
            </div>
            <div className="mt-1 text-muted-foreground text-xs">
              -{rewardSnapshot.discountAmount.toLocaleString("lo-LA")} ₭ ·{" "}
              {rewardSnapshot.pointsCost.toLocaleString("lo-LA")} ຄະແນນ
            </div>
          </div>
        </div>
      )}

      {hasVouchers && (
        <div className="mt-3 space-y-2">
          <div className="text-muted-foreground text-xs">Voucher snapshots</div>
          <ul className="space-y-2">
            {voucherSnapshots?.map((snapshot) => (
              <li
                key={`${snapshot.code}-${snapshot.validatedAt}`}
                className="rounded border bg-background p-2 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{snapshot.code}</span>
                  <Badge variant="outline" className="text-xs">
                    {getVoucherSnapshotSourceLabel(snapshot.source)}
                  </Badge>
                </div>
                <div className="mt-1 text-muted-foreground text-xs">
                  -{snapshot.appliedAmount.toLocaleString("lo-LA")} ₭
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasBreakdown && (
        <div className="mt-3 space-y-2">
          <div className="text-muted-foreground text-xs">Discount breakdown</div>
          <ul className="space-y-1 text-sm">
            {discountBreakdown.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-2"
              >
                <span className="text-muted-foreground">
                  {item.label}
                  {item.code ? ` (${item.code})` : ""}
                </span>
                <span className="text-green-600">
                  -{item.amount.toLocaleString("lo-LA")} ₭
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
